import React from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck,
  MapPin,
  Calendar,
  CreditCard,
  Package,
  BarChart3,
  HelpCircle,
  Megaphone,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useAdminHomeChecklist from '../../hooks/useAdminHomeChecklist';
import ProductHome from '../../components/shared/product/ProductHome';

const ADMIN_CARDS = [
  {
    id: 'vendors',
    title: 'Review vendor verifications',
    description:
      'Approve or follow up on vendor identity checks so the marketplace stays trusted.',
    href: '/admin/vendors',
    cta: 'Open vendors',
    icon: UserCheck,
    badge: 'Priority',
  },
  {
    id: 'markets',
    title: 'Activate booking markets',
    description:
      'Turn markets on for bookable countries and keep tax/settlement settings current.',
    href: '/admin/markets',
    cta: 'Manage markets',
    icon: MapPin,
  },
  {
    id: 'bookings',
    title: 'Monitor bookings',
    description:
      'Track platform and private bookings, disputes, and event status across vendors.',
    href: '/admin/bookings',
    cta: 'View bookings',
    icon: Calendar,
  },
  {
    id: 'payments',
    title: 'Reconcile payments & payouts',
    description:
      'Check deposits, refunds, and vendor payout disbursements when money moves.',
    href: '/admin/payments',
    cta: 'Open payments',
    icon: CreditCard,
  },
  {
    id: 'plans',
    title: 'Configure subscription plans',
    description:
      'Keep vendor plans, pricing, and feature gates aligned with your go-to-market.',
    href: '/admin/subscriptions',
    cta: 'Edit plans',
    icon: Package,
  },
  {
    id: 'pulse',
    title: 'Check platform pulse',
    description:
      'See revenue, booking trends, and system health on the full metrics dashboard.',
    href: '/admin/dashboard',
    cta: 'Open dashboard',
    icon: BarChart3,
  },
  {
    id: 'support',
    title: 'Triage support tickets',
    description:
      'Respond to client and vendor issues before they become escalations.',
    href: '/admin/support-tickets',
    cta: 'Open tickets',
    icon: HelpCircle,
  },
  {
    id: 'announce',
    title: 'Publish an announcement',
    description:
      'Share marketplace updates with vendors from a single announcement feed.',
    href: '/admin/announcements',
    cta: 'Create update',
    icon: Megaphone,
  },
];

const QUICK_LINKS = [
  { label: 'Dashboard', href: '/admin/dashboard' },
  { label: 'Vendors', href: '/admin/vendors' },
  { label: 'Bookings', href: '/admin/bookings' },
  { label: 'Markets', href: '/admin/markets' },
  { label: 'Settings', href: '/admin/settings' },
];

const AdminHome = () => {
  const { user } = useAuth();
  const { visible, dismiss, restore } = useAdminHomeChecklist();
  const name = user?.name?.split(' ')?.[0] || 'Admin';

  return (
    <ProductHome
      brandLabel="vendaList Admin"
      entityLabel="the marketplace"
      subtitle={`Hi ${name} — use these shortcuts to keep vendors, markets, and money healthy.`}
      cards={ADMIN_CARDS}
      quickLinks={QUICK_LINKS}
      checklistVisible={visible}
      onDismissChecklist={dismiss}
      onRestoreChecklist={restore}
      footer={
        <div className="flex flex-col items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3 sm:flex-row">
          <div>
            <p className="text-sm font-medium text-ink">Need the full metrics view?</p>
            <p className="text-xs text-ink-secondary">
              Charts, recent activity, and system health live on the dashboard.
            </p>
          </div>
          <Link
            to="/admin/dashboard"
            className="inline-flex h-8 min-h-8 items-center gap-1.5 rounded-md bg-ink px-3 text-xs font-medium text-white hover:bg-neutral-800"
          >
            Go to dashboard
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      }
    />
  );
};

export default AdminHome;
